import React, { useState } from 'react';
import {
    Card,
    CardBody,
    Typography,
    Button,
    Chip,
    Select,
    Option,
    Avatar,
    IconButton,
    Menu,
    MenuHandler,
    MenuList,
    MenuItem,
    Alert,
    Breadcrumbs,
    Spinner
} from "@material-tailwind/react";
import {
    User,
    Mail,
    Phone,
    Calendar,
    Tag,
    AlertCircle,
    CheckCircle,
    MoreVertical,
    Edit,
    Trash2,
    UserPlus,
    AlertTriangle,
    Info,
    ChevronUp,
    ChevronDown,
    Flag,
    Zap,
    Shield,
    Paperclip,
    Hash,
    HomeIcon,
    LifeBuoyIcon,
    InfoIcon,
    ClockIcon,
    CheckCircleIcon,
    XCircleIcon,
} from "lucide-react";
import moment from 'moment';
import { Link, useParams } from 'react-router-dom';
import { useGetSupportTicketDetails } from '../../custom-hooks/use-support';
import { convertToPresentableText } from '../../utils/functions';


// Helper function to get status color and icon
const getStatusConfig = (status) => {
    switch (status) {
        case 'open':
            return {
                color: 'blue',
                icon: <InfoIcon />
            };
        case 'in_progress':
            return {
                color: 'amber',
                icon: <ClockIcon />
            };
        case 'resolved':
            return {
                color: 'green',
                icon: <CheckCircleIcon />
            };
        case 'closed':
            return {
                color: 'gray',
                icon: <XCircleIcon />
            };
        default:
            return {
                color: 'gray',
                icon: <InfoIcon />
            };
    }
};

// Helper function to get priority config
const getPriorityConfig = (priority) => {
    switch (priority) {
        case 'critical':
            return {
                color: 'red',
                icon: <Zap className="h-4 w-4" />,
                label: 'Critical'
            };
        case 'high':
            return {
                color: 'orange',
                icon: <ChevronUp className="h-4 w-4" />,
                label: 'High'
            };
        case 'medium':
            return {
                color: 'amber',
                icon: <Flag className="h-4 w-4" />,
                label: 'Medium'
            };
        case 'low':
            return {
                color: 'blue',
                icon: <ChevronDown className="h-4 w-4" />,
                label: 'Low'
            };
        default:
            return {
                color: 'gray',
                icon: <Flag className="h-4 w-4" />,
                label: 'Unknown'
            };
    }
};

// Helper function to get category config
const getCategoryConfig = (category) => {
    const categories = {
        'technical': { color: 'purple', icon: <AlertCircle className="h-4 w-4" /> },
        'billing': { color: 'green', icon: <Calendar className="h-4 w-4" /> },
        'general': { color: 'blue', icon: <Info className="h-4 w-4" /> },
        'complaint': { color: 'red', icon: <AlertTriangle className="h-4 w-4" /> },
        'feature_request': { color: 'indigo', icon: <ChevronUp className="h-4 w-4" /> }
    };

    return categories[category] || { color: 'gray', icon: <Tag className="h-4 w-4" /> };
};

// Format date
const formatDate = (date) => {
    return moment(date).format('MMM DD, YYYY hh:mm A');
};

// Format time ago
const formatTimeAgo = (date) => {
    return moment(date).fromNow();
};

// Main Support Ticket Detail Page
const SupportDetailPage = () => {
    const { ticketId } = useParams();

    const { data, isLoading, error: isError } = useGetSupportTicketDetails(ticketId);
    const ticket = data?.ticket || {};
    console.log('ticket', ticket);

    const [selectedStatus, setSelectedStatus] = useState('open');

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
                    <Typography className="mt-4 text-gray-600">Loading ticket details...</Typography>
                </div>
            </div>
        );
    }

    if (isError) {
        return (
            <Alert color="red" className="m-4">
                <AlertCircle className="h-4 w-4" />
                <span className="ml-2">Error loading ticket details. Please try again.</span>
            </Alert>
        );
    }

    const statusConfig = getStatusConfig(ticket.status);
    const priorityConfig = getPriorityConfig(ticket.priority);
    const categoryConfig = getCategoryConfig(ticket.category);

    return (
        <div className="p-4 m-4 border rounded-3xl">
            <div className="">
                <Breadcrumbs className="mb-4">
                    <Link to="/" className="opacity-60 flex items-center gap-1">
                        <HomeIcon size={14} />
                        <span>Home</span>
                    </Link>
                    <Link to="/support" className="opacity-60 flex items-center gap-1">
                        <LifeBuoyIcon size={14} />
                        <span>Support</span>
                    </Link>
                    <span className="flex items-center gap-1">
                        {isLoading ? (
                            <Spinner size="sm" />
                        ) : (
                            <>
                                <LifeBuoyIcon size={14} />
                                <span>{convertToPresentableText(ticket?.subject.slice(0, 20)) || 'Ticket Details'}</span>
                                {ticket?.subject.length > 20 && <span>...</span>}
                            </>
                        )}
                    </span>
                </Breadcrumbs>
            </div>
            <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
                <div className='col-span-2 space-y-4'>
                    <div>
                        <Typography variant="small" className="text-gray-500 mb-2">Subject</Typography>
                        <Typography variant="h5" className="font-medium">
                            {ticket.subject}
                        </Typography>
                    </div>
                    <div>
                        <Typography variant="small" className="text-gray-500 mb-2">Description</Typography>
                        <Typography className="text-gray-700">
                            {ticket.description}
                        </Typography>
                    </div>
                    {/* Status Bar */}
                    <div className="flex items-center gap-2">
                        <div className="flex flex-col items-center gap-2 bg-gray-100 rounded-lg px-4 py-2">
                            <div className="text-gray-600 text-xs">Status:</div>
                            <Chip
                                value={ticket.status.charAt(0).toUpperCase() + ticket.status.slice(1)}
                                className='rounded-full'
                                color={statusConfig.color}
                            // icon={statusConfig.icon}
                            />
                        </div>

                        <div className="flex flex-col items-center gap-2 bg-gray-100 rounded-lg px-4 py-2">
                            <div className="text-gray-600 text-xs">Priority:</div>
                            <Chip
                                value={priorityConfig.label}
                                className='rounded-full'
                                color={priorityConfig.color}
                            // icon={priorityConfig.icon}
                            />
                        </div>

                        <div className="flex flex-col items-center gap-2 bg-gray-100 rounded-lg px-4 py-2">
                            <div className="text-gray-600 text-xs">Category:</div>
                            <Chip
                                value={ticket.category.charAt(0).toUpperCase() + ticket.category.slice(1)}
                                className='rounded-full'
                                color={categoryConfig.color}
                            // icon={categoryConfig.icon}
                            />
                        </div>
                    </div>
                    {ticket.attachments.length > 0 && (
                        <div className="mt-4">
                            <Typography variant="small" className="text-gray-500 mb-2">Attachments</Typography>
                            <div className="flex flex-wrap gap-2">
                                {ticket.attachments.map((attachment, index) => (
                                    <Chip
                                        key={index}
                                        value={`Attachment ${index + 1}`}
                                        icon={<Paperclip className="h-3 w-3" />}
                                        variant="outlined"
                                        size="sm"
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {ticket.tags.length > 0 && (
                        <div className="mt-4">
                            <Typography variant="small" className="text-gray-500 mb-2">Tags</Typography>
                            <div className="flex flex-wrap gap-2">
                                {ticket.tags.map((tag, index) => (
                                    <Chip
                                        key={index}
                                        value={tag}
                                        icon={<Tag className="h-3 w-3" />}
                                        variant="outlined"
                                        size="sm"
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                    <hr className='my-4' />
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Typography variant="small" className="text-gray-500 mb-1">Created Date</Typography>
                            <Typography className="text-sm">{formatDate(ticket.createdDate)}</Typography>
                        </div>
                        <div>
                            <Typography variant="small" className="text-gray-500 mb-1">Last Updated</Typography>
                            <Typography className="text-sm">{formatDate(ticket.lastUpdated)}</Typography>
                        </div>
                    </div>
                    <hr className='my-4' />
                    {/* Additional Information Card */}
                    <div>
                        <Typography variant="h6" className="mb-4">Additional Information</Typography>

                        <div className="grid grid-cols-2 gap-4">
                            <div className='space-y-2'>
                                <div className="p-3 bg-gray-50 rounded-lg">
                                    <Typography variant="small" className="text-gray-500 mb-1">Escalation Level</Typography>
                                    <div className="flex items-center gap-2">
                                        <Shield className="h-4 w-4 text-gray-600" />
                                        <Typography className="font-medium">Level {ticket.escalationLevel}</Typography>
                                    </div>
                                </div>

                                <div className="p-3 bg-gray-50 rounded-lg">
                                    <Typography variant="small" className="text-gray-500 mb-1">Customer Acknowledgment</Typography>
                                    <Chip
                                        value={ticket.customerAcknowledgmentStatus === 'pending' ? 'Pending' : 'Acknowledged'}
                                        color={ticket.customerAcknowledgmentStatus === 'pending' ? 'amber' : 'green'}
                                        size="sm"
                                    />
                                </div>

                                <div className="p-3 bg-gray-50 rounded-lg">
                                    <Typography variant="small" className="text-gray-500 mb-1">Requires Acknowledgment</Typography>
                                    <Chip
                                        value={ticket.requiresCustomerAcknowledgment ? 'Yes' : 'No'}
                                        color={ticket.requiresCustomerAcknowledgment ? 'blue' : 'gray'}
                                        size="sm"
                                    />
                                </div>

                                <div className="p-3 bg-gray-50 rounded-lg">
                                    <Typography variant="small" className="text-gray-500 mb-1">Customer Feedback</Typography>
                                    <Chip
                                        value={ticket.customerFeedbackSubmitted ? 'Submitted' : 'Pending'}
                                        color={ticket.customerFeedbackSubmitted ? 'green' : 'gray'}
                                        size="sm"
                                    />
                                </div>
                            </div>
                            <div className='space-y-2'>
                                {/* Agent Assignment */}
                                <Card className='rounded-lg shadow-none border'>
                                    <CardBody>
                                        <div className="flex justify-between items-start mb-4">
                                            <Typography variant="h6" className="flex items-center">
                                                <UserPlus className="h-5 w-5 mr-2" />
                                                Assigned Agent
                                            </Typography>
                                            <IconButton variant="text" size="sm">
                                                <Edit className="h-4 w-4" />
                                            </IconButton>
                                        </div>

                                        {ticket.assignedAgent ? (
                                            <div className="space-y-3">
                                                <div className="flex items-center">
                                                    <Avatar
                                                        src={`https://ui-avatars.com/api/?name=${ticket.assignedAgent.name}&background=0ea5e9&color=fff`}
                                                        alt={ticket.assignedAgent.name}
                                                        size="sm"
                                                        className="mr-3"
                                                    />
                                                    <div>
                                                        <Link to={`/user/${ticket.assignedAgent._id}`} className='underline hover:text-blue-600 duration-300'>
                                                            <Typography className="font-medium ">{ticket.assignedAgent.name}</Typography>
                                                        </Link>
                                                        <Typography variant="small" className="text-gray-500 text-blue-600">{ticket.assignedAgent.email}</Typography>
                                                    </div>
                                                </div>

                                                <div>
                                                    <Typography variant="small" className="text-gray-500 mb-1">Agent ID</Typography>
                                                    <Link to={`/user/${ticket.assignedAgent._id}`} className='text-gray-600 underline hover:text-blue-600 duration-300'>
                                                        <Typography className="font-mono text-xs">
                                                            {ticket.assignedAgent._id}
                                                        </Typography>
                                                    </Link>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="text-center py-4">
                                                <Typography variant="small" className="text-gray-500 mb-3">
                                                    No agent assigned
                                                </Typography>
                                                <Button size="sm" color="blue">
                                                    Assign Agent
                                                </Button>
                                            </div>
                                        )}
                                    </CardBody>
                                </Card>
                            </div>
                        </div>
                    </div>
                </div>
                <div className='space-y-4'>
                    {/* Customer Information */}
                    <Card className='rounded-lg shadow-none border'>
                        <CardBody>
                            <Typography variant="h6" className="mb-4 flex items-center">
                                <User className="h-5 w-5 mr-2" />
                                Customer Information
                            </Typography>

                            <div className="space-y-3">
                                <div>
                                    <Typography variant="small" className="text-gray-500 mb-1">Name</Typography>
                                    <Link to={`/customer/${ticket.customer._id}`} className='underline hover:text-blue-600 duration-300'>
                                        <Typography className="font-medium">{ticket.customer.name}</Typography>
                                    </Link>
                                </div>

                                <div>
                                    <Typography variant="small" className="text-gray-500 mb-1">Email</Typography>
                                    <div className="flex items-center">
                                        <Mail className="h-4 w-4 mr-2 text-gray-600" />
                                        <a href={`mailto:${ticket.customer.email}`} className="text-blue-600 hover:underline text-sm">
                                            {ticket.customer.email}
                                        </a>
                                    </div>
                                </div>

                                <div>
                                    <Typography variant="small" className="text-gray-500 mb-1">Phone</Typography>
                                    <div className="flex items-center">
                                        <Phone className="h-4 w-4 mr-2 text-gray-600" />
                                        <a href={`tel:${ticket.customer.phone}`} className="text-blue-600 hover:underline text-sm">
                                            {ticket.customer.phone}
                                        </a>
                                    </div>
                                </div>

                                <div>
                                    <Typography variant="small" className="text-gray-500 mb-1">Customer ID</Typography>
                                    <Link to={`/customer/${ticket.customer._id}`} className='text-gray-600 underline hover:text-blue-600 duration-300'>
                                        <Typography className="font-mono text-xs">
                                            {ticket.customer._id}
                                        </Typography>
                                    </Link>
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                    {/* Quick Actions */}
                    <Card className='rounded-lg shadow-none border'>
                        <CardBody>
                            <Typography variant="h6" className="mb-4">Quick Actions</Typography>
                            <div className="space-y-2">
                                <Select
                                    label="Update Status"
                                    value={selectedStatus}
                                    onChange={(val) => setSelectedStatus(val)}
                                >
                                    <Option value="open">Open</Option>
                                    <Option value="in_progress">In Progress</Option>
                                    <Option value="resolved">Resolved</Option>
                                    <Option value="closed">Closed</Option>
                                </Select>

                                <Button variant="outlined" className="w-full flex items-center justify-center gap-2">
                                    <ChevronUp className="h-4 w-4" />
                                    Escalate Ticket
                                </Button>

                                <Button variant="outlined" color="green" className="w-full flex items-center justify-center gap-2">
                                    <CheckCircle className="h-4 w-4" />
                                    Mark as Resolved
                                </Button>
                            </div>
                        </CardBody>
                    </Card>
                    {/* Ticket Metadata */}
                    <Card className='rounded-lg shadow-none border'>
                        <CardBody>
                            <Typography variant="h6" className="mb-4 flex items-center">
                                <Hash className="h-5 w-5 mr-2" />
                                Ticket Metadata
                            </Typography>

                            <div className="space-y-3 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-gray-500">Ticket ID</span>
                                    <span className="font-mono text-xs">{ticket._id}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-500">Version</span>
                                    <span>{ticket.__v}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-500">Created</span>
                                    <span>{formatTimeAgo(ticket.createdDate)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-500">Updated</span>
                                    <span>{formatTimeAgo(ticket.lastUpdated)}</span>
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </div>

            </div>
            {/* Header */}
            <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                        <Menu>
                            <MenuHandler>
                                <IconButton variant="text">
                                    <MoreVertical className="h-5 w-5" />
                                </IconButton>
                            </MenuHandler>
                            <MenuList>
                                <MenuItem className="flex items-center gap-2">
                                    <Edit className="h-4 w-4" />
                                    Edit Ticket
                                </MenuItem>
                                <MenuItem className="flex items-center gap-2">
                                    <UserPlus className="h-4 w-4" />
                                    Reassign Agent
                                </MenuItem>
                                <MenuItem className="flex items-center gap-2">
                                    <ChevronUp className="h-4 w-4" />
                                    Escalate
                                </MenuItem>
                                <MenuItem className="flex items-center gap-2 text-red-600">
                                    <Trash2 className="h-4 w-4" />
                                    Delete Ticket
                                </MenuItem>
                            </MenuList>
                        </Menu>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SupportDetailPage;